import { entityService } from '../../entityService';
import { createFileLogger } from '../../../utils/logger';

const log = createFileLogger('INTEGRATION_RESOLVER');

/**
 * Resolve user's Google Drive integration ID
 * @param userId - User ID to find integration for
 * @returns Integration entity ID
 * @throws Error if no connected Google Drive integration found
 */
export async function resolveGoogleDriveIntegration(userId: string): Promise<string> {
  try {
    log.log('[resolveGoogleDriveIntegration] Finding Google Drive integration for user:', userId);
    
    const entities = await entityService.getAllUserEntities();
    
    const integration = entities.find((e: any) => 
      (e.entityType === 'Integration' || e.entityType?.startsWith('Integration.')) && 
      e.skeleton?.integrationType === 'GoogleDrive' &&
      e.skeleton?.status === 'connected'
    );
    
    if (!integration) {
      log.error('[resolveGoogleDriveIntegration] No connected Google Drive integration found');
      throw new Error('No connected Google Drive integration found. Please connect Google Drive in Settings → Integrations.');
    }
    
    log.log('[resolveGoogleDriveIntegration] Found integration:', integration._id);
    return integration._id;
  } catch (error) {
    log.error('[resolveGoogleDriveIntegration] Error resolving integration:', error);
    throw error;
  }
}

/**
 * Resolve user's WordPress integration ID
 * @param userId - User ID to find integration for
 * @returns Integration entity ID
 * @throws Error if no connected WordPress integration found
 */
export async function resolveWordPressIntegration(userId: string): Promise<string> {
  try {
    log.log('[resolveWordPressIntegration] Finding WordPress integration for user:', userId);
    
    const entities = await entityService.getAllUserEntities();
    
    const integration = entities.find((e: any) => 
      (e.entityType === 'Integration' || e.entityType?.startsWith('Integration.')) && 
      e.skeleton?.integrationType === 'WordPress' &&
      e.skeleton?.status === 'connected'
    );
    
    if (!integration) {
      log.error('[resolveWordPressIntegration] No connected WordPress integration found');
      throw new Error('No connected WordPress integration found. Please connect WordPress in Settings → Integrations.');
    }
    
    log.log('[resolveWordPressIntegration] Found integration:', integration._id);
    return integration._id;
  } catch (error) {
    log.error('[resolveWordPressIntegration] Error resolving integration:', error);
    throw error;
  }
}

/**
 * Resolve user's integration by type
 * @param userId - User ID to find integration for
 * @param integrationType - Integration type (e.g., 'GoogleDrive', 'Dropbox')
 * @returns Integration entity ID
 * @throws Error if no connected integration found
 */
export async function resolveIntegration(userId: string, integrationType: string): Promise<string> {
  try {
    log.log(`[resolveIntegration] Finding ${integrationType} integration for user:`, userId);
    
    const entities = await entityService.getAllUserEntities();
    
    const integration = entities.find((e: any) => 
      (e.entityType === 'Integration' || e.entityType?.startsWith('Integration.')) && 
      e.skeleton?.integrationType === integrationType &&
      e.skeleton?.status === 'connected'
    );
    
    if (!integration) {
      log.error(`[resolveIntegration] No connected ${integrationType} integration found`);
      throw new Error(`No connected ${integrationType} integration found. Please connect ${integrationType} in Settings → Integrations.`);
    }
    
    log.log('[resolveIntegration] Found integration:', integration._id);
    return integration._id;
  } catch (error) {
    log.error('[resolveIntegration] Error resolving integration:', error);
    throw error;
  }
}

